import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2, CheckCircle2, Mail, AlertCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState(location.state?.email || "");
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyEmail(token);
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    setVerifying(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/verify-email`, { token });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.error || "Verification failed");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Verification failed. The link may have expired.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError("Email address is required");
      return;
    }

    setLoading(true);
    setError("");
    setResendSuccess(false);

    try {
      const response = await axios.post(`${API}/customer/auth/resend-verification`, { email });

      if (response.data.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setError(response.data.error || "Failed to resend email");
      }
    } catch (err) {
      console.error("Resend error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Failed to resend email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="glass-heavy rounded-3xl p-8 border border-white/10 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-heading font-semibold text-white mb-2">
              Email Verified!
            </h1>
            <p className="text-slate-300 mb-6">
              Your account is now active. Redirecting to login...
            </p>
            <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Redirecting...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="glass-heavy rounded-3xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-xl gradient-active flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-semibold text-white">QuantumFlowSync</span>
            </div>
            <h1 className="text-3xl font-heading font-semibold text-white mb-2">
              Verify your email
            </h1>
            {email ? (
              <p className="text-slate-300">
                We sent a verification link to <strong className="text-white">{email}</strong>
              </p>
            ) : (
              <p className="text-slate-300">
                Enter your email to resend the verification link
              </p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {resendSuccess && (
            <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/20 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-green-400 text-sm">Verification email sent! Please check your inbox.</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-blue-400 text-sm">
                📧 Check your inbox and click the verification link to activate your account.
              </p>
            </div>

            {!email && (
              <div>
                <Label htmlFor="email" className="text-white">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                />
              </div>
            )}

            <Button
              onClick={handleResend}
              disabled={loading || !email}
              variant="outline"
              className="w-full h-12 rounded-full border-white/10 bg-white/5 hover:bg-white/10 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                "Resend Verification Email"
              )}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              Didn't receive the email? Check your spam folder or try resending.
            </p>
          </div>
        </div>

        <div className="text-center mt-6 space-y-2">
          <button 
            onClick={() => navigate('/login')} 
            className="text-slate-400 hover:text-white transition-colors text-sm block mx-auto"
          >
            ← Back to login
          </button>
          <button 
            onClick={() => navigate('/')} 
            className="text-slate-400 hover:text-white transition-colors text-sm block mx-auto"
          >
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
